import type { SVGProps } from 'react'

/**
 * Ícones inline, traço de 1.6px, para bater com o desenho fino das telas de
 * referência. Sem biblioteca externa: cada ícone entra no bundle só se usado.
 */

type IconProps = SVGProps<SVGSVGElement>

function Icon({ children, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      {children}
    </svg>
  )
}

export const SearchIcon = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </Icon>
)

export const ChevronDownIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="m6 9 6 6 6-6" />
  </Icon>
)

export const ChevronRightIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="m9 6 6 6-6 6" />
  </Icon>
)

export const ChevronLeftIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="m15 6-6 6 6 6" />
  </Icon>
)

export const ArrowLeftIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M19 12H5" />
    <path d="m11 18-6-6 6-6" />
  </Icon>
)

export const CloseIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M18 6 6 18M6 6l12 12" />
  </Icon>
)

/** Sacola do cabeçalho e do botão "marcar como comprado". */
export const BagIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M6 8h12l-1 12H7L6 8Z" />
    <path d="M9 8V6a3 3 0 0 1 6 0v2" />
  </Icon>
)

/** Sacola com "check" — estado já comprado. */
export const BagCheckIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M6 8h12l-1 12H7L6 8Z" />
    <path d="M9 8V6a3 3 0 0 1 6 0v2" />
    <path d="m9.6 13.6 1.8 1.8 3.2-3.4" />
  </Icon>
)

/** Sacola com "+" — ação de adicionar compra (hover do card). */
export const BagPlusIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M6 8h12l-1 12H7L6 8Z" />
    <path d="M9 8V6a3 3 0 0 1 6 0v2" />
    <path d="M12 12v5M9.5 14.5h5" />
  </Icon>
)

export const ShareIcon = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="18" cy="5" r="2.6" />
    <circle cx="6" cy="12" r="2.6" />
    <circle cx="18" cy="19" r="2.6" />
    <path d="m8.4 10.8 7.2-4.2M8.4 13.2l7.2 4.2" />
  </Icon>
)

export const InfoIcon = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 8h.01" />
  </Icon>
)

export const HomeIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-4v-6H9v6H5a1 1 0 0 1-1-1v-9.5Z" />
  </Icon>
)

export const IdCardIcon = (props: IconProps) => (
  <Icon {...props}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <circle cx="9" cy="11" r="2" />
    <path d="M6 16c.6-1.4 1.7-2 3-2s2.4.6 3 2M14.5 10h4M14.5 13.5h4" />
  </Icon>
)

export const ShieldIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 3.5 19 6v6c0 4-3 7-7 8.5C8 19 5 16 5 12V6l7-2.5Z" />
    <path d="m9.5 12 1.8 1.8 3.4-3.6" />
  </Icon>
)

export const UserIcon = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="8" r="3.4" />
    <path d="M5 20c.9-3.4 3.6-5 7-5s6.1 1.6 7 5" />
  </Icon>
)

export const MapPinIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 21s7-5.2 7-10.4A7 7 0 0 0 5 10.6C5 15.8 12 21 12 21Z" />
    <circle cx="12" cy="10.5" r="2.5" />
  </Icon>
)

export const BriefcaseIcon = (props: IconProps) => (
  <Icon {...props}>
    <rect x="3" y="7.5" width="18" height="12" rx="2" />
    <path d="M9 7.5V6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v1.5M3 12.5h18" />
  </Icon>
)

export const PhoneIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M6.5 3.5h3l1.5 4-2 1.4a12 12 0 0 0 6.1 6.1l1.4-2 4 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 4.5 5.7a2 2 0 0 1 2-2.2Z" />
  </Icon>
)

export const MailIcon = (props: IconProps) => (
  <Icon {...props}>
    <rect x="3" y="5.5" width="18" height="13" rx="2" />
    <path d="m3.5 7.5 8.5 6 8.5-6" />
  </Icon>
)

export const KeyDotsIcon = (props: IconProps) => (
  <Icon {...props} strokeWidth={2.4}>
    <path d="M7 12h.01M12 12h.01M17 12h.01" />
  </Icon>
)

export const CheckCircleIcon = (props: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <circle cx="12" cy="12" r="10" fill="currentColor" />
    <path
      d="m7.8 12.2 2.8 2.8 5.6-5.8"
      stroke="#fff"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

export const PlusIcon = (props: IconProps) => (
  <Icon {...props} strokeWidth={2}>
    <path d="M12 5v14M5 12h14" />
  </Icon>
)

export const TrashIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 7h16M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7" />
    <path d="M6.5 7 7.5 20h9L17.5 7M10.5 10.5v6M13.5 10.5v6" />
  </Icon>
)

export const CameraIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z" />
    <circle cx="12" cy="13" r="3.2" />
  </Icon>
)

export const MenuIcon = (props: IconProps) => (
  <Icon {...props} strokeWidth={2}>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </Icon>
)

export const MegaphoneIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 10v4a1 1 0 0 0 1 1h2l9 4V5L7 9H5a1 1 0 0 0-1 1Z" />
    <path d="M18.5 9.5a3.5 3.5 0 0 1 0 5" />
  </Icon>
)

export const CursorClickIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M9 4.5 19 12l-4.4 1.1L12.6 19 9 4.5Z" />
    <path d="M4.5 9h-2M6 5.2 4.6 3.8M6 12.8l-1.4 1.4" />
  </Icon>
)

export const CartIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M3 5h2l2.2 9.6a1.5 1.5 0 0 0 1.5 1.2h7.6a1.5 1.5 0 0 0 1.5-1.2L19.5 8H6" />
    <circle cx="9.5" cy="19" r="1.3" />
    <circle cx="17" cy="19" r="1.3" />
  </Icon>
)

export const CheckSquareIcon = (props: IconProps) => (
  <Icon {...props}>
    <path d="M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9" />
    <path d="m8.5 11.5 3 3L21 5" />
  </Icon>
)

/** Estrela do bloco de avaliação. `filled` controla o preenchimento. */
export function StarIcon({ filled = false, ...props }: IconProps & { filled?: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path d="m12 3.5 2.6 5.6 6 .8-4.4 4.2 1.1 6-5.3-2.9-5.3 2.9 1.1-6L3.4 9.9l6-.8L12 3.5Z" />
    </svg>
  )
}

export const FireIcon = (props: IconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M12 2.5c.6 3-1.2 4.4-2.6 5.8C7.7 9.9 6 11.5 6 14a6 6 0 0 0 12 0c0-2.6-1.3-4.3-2.6-6-.6 .8-1.2 1.2-1.9 1.2 .7-2.6-.4-5.2-1.5-6.7Z" />
  </svg>
)

/** Caixa vazia da lista de compras. Ilustração, não ícone de UI. */
export const EmptyBoxIllustration = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 96 80" fill="none" aria-hidden="true" {...props}>
    <ellipse cx="48" cy="70" rx="30" ry="4.5" fill="#000" opacity="0.08" />
    <path d="M14 30h68l-6 33a4 4 0 0 1-4 3.4H24a4 4 0 0 1-4-3.4L14 30Z" fill="#ffc107" />
    <path d="M14 30h68l-6 8H20l-6-8Z" fill="#ffd76a" />
    <path
      d="M20 22h56a3 3 0 0 1 3 3v5H17v-5a3 3 0 0 1 3-3Z"
      fill="#e6a600"
    />
    <path d="M40 38h16" stroke="#b98900" strokeWidth="3" strokeLinecap="round" />
  </svg>
)
